/**
 * Gráficos SVG hechos a mano (sin librerías).
 *
 * Criterios: ejes con pocas marcas, sin sombras ni 3D, una sola familia
 * tipográfica (la del portal), colores sobrios con contraste sobre fondo
 * oscuro, y el número SIEMPRE disponible — como etiqueta si entra, y en el
 * `<title>` nativo del SVG (tooltip del browser, gratis y accesible) siempre.
 *
 * Los componentes son tontos: reciben data ya calculada por `src/lib/charts.ts`
 * y `src/lib/projections.ts`. Acá no se agrega ni se filtra nada.
 */
import { useT } from '../i18n/use-t';
import { HEAT_STEPS, heatBucket, heatVar, linePath, linearScale, niceTicks } from '../lib/charts';
import { effPct } from '../lib/format';

export type Side = 'home' | 'away';

// ---------------------------------------------------------------------------
// Barras horizontales
// ---------------------------------------------------------------------------

export interface BarDatum {
  label: string;
  value: number;
  /** Texto del tooltip; si falta se usa `label: valor`. */
  title?: string;
  /** Texto a la derecha de la barra; si falta, el valor formateado. */
  display?: string;
}

/**
 * Barras horizontales con track de fondo. `max` fija el dominio (por ejemplo 1
 * para porcentajes) para que dos equipos se puedan comparar lado a lado.
 */
export function BarChart({
  data,
  side,
  max = 1,
  format = (v: number) => effPct(v),
  emptyLabel,
}: {
  data: readonly BarDatum[];
  side: Side;
  max?: number;
  format?: (value: number) => string;
  emptyLabel?: string;
}): JSX.Element {
  const t = useT();
  if (data.length === 0) return <p className="empty">{emptyLabel ?? t('common.noData')}</p>;

  const rowHeight = 22;
  const labelWidth = 34;
  const valueWidth = 52;
  const width = 320;
  const trackX = labelWidth;
  const trackWidth = width - labelWidth - valueWidth;
  const height = data.length * rowHeight + 16;
  const scale = linearScale([0, Math.max(max, 0.0001)], [0, trackWidth]);

  return (
    <svg
      className="chart bar-chart"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={t('evolution.barChartLabel')}
    >
      {/* Marcas verticales: pocas y redondas. */}
      {niceTicks(0, max, 4).map((tick) => (
        <line
          key={tick}
          className="grid-line"
          x1={trackX + scale(tick)}
          x2={trackX + scale(tick)}
          y1={2}
          y2={data.length * rowHeight + 2}
        />
      ))}
      {data.map((row, i) => {
        const y = i * rowHeight + 4;
        const w = Math.max(0, scale(Math.max(0, Math.min(row.value, max))));
        return (
          <g key={row.label}>
            <title>{row.title ?? `${row.label}: ${format(row.value)}`}</title>
            <text x={0} y={y + 11} dominantBaseline="middle">
              {row.label}
            </text>
            <rect className="bar-track" x={trackX} y={y} width={trackWidth} height={14} rx={2} />
            <rect className={`bar ${side}`} x={trackX} y={y} width={w} height={14} rx={2} />
            <text
              className="value-label"
              x={width}
              y={y + 11}
              textAnchor="end"
              dominantBaseline="middle"
            >
              {row.display ?? format(row.value)}
            </text>
          </g>
        );
      })}
      <line
        className="axis-line"
        x1={trackX}
        x2={trackX}
        y1={2}
        y2={data.length * rowHeight + 2}
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Barras apiladas (distribución del armador)
// ---------------------------------------------------------------------------

export interface StackSegment {
  key: string;
  value: number;
  title: string;
}

/** Una barra 100% con un segmento por destino, ordenados por volumen. */
export function StackedBar({ segments }: { segments: readonly StackSegment[] }): JSX.Element {
  const t = useT();
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  if (total <= 0) return <p className="empty">{t('setter.noSetAttacks')}</p>;

  const width = 320;
  const height = 26;
  let x = 0;

  return (
    <svg
      className="chart bar-chart"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={t('evolution.distributionLabel')}
    >
      {segments.map((segment, i) => {
        const w = (segment.value / total) * width;
        const rect = (
          <g key={segment.key}>
            <title>{segment.title}</title>
            <rect
              className="stack-seg"
              x={x}
              y={0}
              width={w}
              height={16}
              fill={heatVar(HEAT_STEPS - 1 - (i % HEAT_STEPS))}
            />
            {w > 30 ? (
              <text
                className={`value-label${HEAT_STEPS - 1 - (i % HEAT_STEPS) >= 3 ? ' light' : ''}`}
                x={x + w / 2}
                y={9}
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {segment.key}
              </text>
            ) : null}
            {w > 46 ? (
              <text x={x + w / 2} y={24} textAnchor="middle">
                {Math.round((segment.value / total) * 100)}%
              </text>
            ) : null}
          </g>
        );
        x += w;
        return rect;
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Heatmap (matriz de zonas)
// ---------------------------------------------------------------------------

export interface HeatCell {
  row: number | null;
  col: number | null;
  value: number;
  count: number;
  title: string;
}

/**
 * Matriz zona de salida × zona de llegada. El color es una rampa SECUENCIAL
 * sobre la eficiencia observada (min→max de la propia matriz): con muestras
 * chicas una escala fija pintaría todo igual.
 */
export function Heatmap({
  rows,
  cols,
  cells,
  min,
  max,
  rowLabel,
  colLabel,
}: {
  rows: ReadonlyArray<number | null>;
  cols: ReadonlyArray<number | null>;
  cells: readonly HeatCell[];
  min: number;
  max: number;
  rowLabel?: string;
  colLabel?: string;
}): JSX.Element {
  const t = useT();
  const rowText = rowLabel ?? t('zones.axisRow');
  const colText = colLabel ?? t('zones.axisCol');

  if (rows.length === 0 || cols.length === 0) {
    return <p className="empty">{t('zones.emptyMatrix')}</p>;
  }

  const cell = 40;
  const head = 26;
  const width = head + cols.length * cell;
  const height = head + rows.length * cell + 14;
  const byKey = new Map(cells.map((c) => [`${c.row ?? '-'}x${c.col ?? '-'}`, c]));
  const label = (zone: number | null): string =>
    zone === null ? t('zones.noZoneData') : String(zone);

  return (
    <svg
      className="chart"
      viewBox={`0 0 ${width} ${height}`}
      style={{ maxWidth: width }}
      role="img"
      aria-label={t('zones.matrixLabel', { rowLabel: rowText, colLabel: colText })}
    >
      <text x={0} y={10}>
        {rowText} ↓
      </text>
      <text x={head} y={10}>
        {colText} →
      </text>
      {cols.map((col, ci) => (
        <text key={`c${label(col)}`} x={head + ci * cell + cell / 2} y={head - 6} textAnchor="middle">
          {label(col)}
        </text>
      ))}
      {rows.map((row, ri) => (
        <text key={`r${label(row)}`} x={head - 6} y={head + ri * cell + cell / 2} textAnchor="end" dominantBaseline="middle">
          {label(row)}
        </text>
      ))}
      {rows.map((row, ri) =>
        cols.map((col, ci) => {
          const found = byKey.get(`${row ?? '-'}x${col ?? '-'}`);
          const x = head + ci * cell;
          const y = head + ri * cell;
          if (!found || found.count === 0) {
            return (
              <rect
                key={`${label(row)}-${label(col)}`}
                className="heat-cell"
                x={x}
                y={y}
                width={cell}
                height={cell}
                fill="var(--bg-raised)"
                opacity={0.5}
              />
            );
          }
          return (
            <g key={`${label(row)}-${label(col)}`}>
              <title>{found.title}</title>
              <rect
                className="heat-cell"
                x={x}
                y={y}
                width={cell}
                height={cell}
                fill={heatVar(heatBucket(found.value, min, max))}
              />
              {/* Tinta según el paso de la rampa: blanca sobre los tres oscuros. */}
              <text
                className={`heat-label${heatBucket(found.value, min, max) >= 3 ? ' light' : ''}`}
                x={x + cell / 2}
                y={y + cell / 2 - 4}
                textAnchor="middle"
              >
                {effPct(found.value)}
              </text>
              <text
                className={heatBucket(found.value, min, max) >= 3 ? 'heat-count light' : 'heat-count'}
                x={x + cell / 2}
                y={y + cell / 2 + 9}
                textAnchor="middle"
              >
                n={found.count}
              </text>
            </g>
          );
        }),
      )}
    </svg>
  );
}

/** Leyenda de la rampa secuencial, con los extremos escritos. */
export function HeatLegend({ min, max }: { min: number; max: number }): JSX.Element {
  const t = useT();
  return (
    <div className="legend">
      <span>{t('zones.legend')}</span>
      <span className="heat-scale">
        <span>{effPct(min)}</span>
        {Array.from({ length: HEAT_STEPS }, (_, i) => (
          <span key={i} className="step" style={{ background: heatVar(i) }} />
        ))}
        <span>{effPct(max)}</span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Línea (evolución del marcador)
// ---------------------------------------------------------------------------

export interface ScoreLinePoint {
  home: number;
  away: number;
}

/** Dos líneas (local / visitante) sobre el eje "puntos jugados en el set". */
export function ScoreLineChart({
  points,
  homeName,
  awayName,
  set,
}: {
  points: readonly ScoreLinePoint[];
  homeName: string;
  awayName: string;
  set: number;
}): JSX.Element {
  const t = useT();
  if (points.length <= 1) return <p className="empty">{t('evolution.emptySet', { set })}</p>;

  const width = 480;
  const height = 200;
  const pad = { top: 10, right: 10, bottom: 22, left: 26 };
  const maxScore = points.reduce((max, p) => Math.max(max, p.home, p.away), 0);
  const x = linearScale([0, points.length - 1], [pad.left, width - pad.right]);
  const y = linearScale([0, Math.max(maxScore, 1)], [height - pad.bottom, pad.top]);

  const homePath = linePath(points.map((p, i) => ({ x: x(i), y: y(p.home) })));
  const awayPath = linePath(points.map((p, i) => ({ x: x(i), y: y(p.away) })));
  const last = points[points.length - 1];

  return (
    <svg
      className="chart"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={t('evolution.chartLabel', { set })}
    >
      <title>
        {t('evolution.chartTitle', {
          set,
          homeName,
          home: last?.home ?? 0,
          awayName,
          away: last?.away ?? 0,
        })}
      </title>
      {niceTicks(0, Math.max(maxScore, 1), 4).map((tick) => (
        <g key={tick}>
          <line className="grid-line" x1={pad.left} x2={width - pad.right} y1={y(tick)} y2={y(tick)} />
          <text x={pad.left - 5} y={y(tick)} textAnchor="end" dominantBaseline="middle">
            {tick}
          </text>
        </g>
      ))}
      <line
        className="axis-line"
        x1={pad.left}
        x2={width - pad.right}
        y1={height - pad.bottom}
        y2={height - pad.bottom}
      />
      <path d={homePath} fill="none" stroke="var(--home)" strokeWidth={2} />
      <path d={awayPath} fill="none" stroke="var(--away)" strokeWidth={2} />
      <text x={pad.left} y={height - 6}>
        {t('evolution.firstRally')}
      </text>
      <text x={width - pad.right} y={height - 6} textAnchor="end">
        {t('evolution.lastRally', { count: points.length - 1 })}
      </text>
    </svg>
  );
}
